import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaxRegionKey } from '@capintel/instruments-lib';

import { updateComparisonTaxSettings } from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';
import { PartialUsComparisonTaxSettings, PartialCaComparisonTaxSettings } from '@/types/tax-analysis';

import { US_DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE, US_DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE } from '../../constants';
import { Dispatch } from '@/redux';

/** Default US tax settings */
const DEFAULT_US_SETTINGS: PartialUsComparisonTaxSettings = {
  shortTermCapitalGainsRate: US_DEFAULT_SHORT_TERM_CAPITAL_GAINS_RATE * 100,
  longTermCapitalGainsRate: US_DEFAULT_LONG_TERM_CAPITAL_GAINS_RATE * 100,
};

/** Default CA tax settings */
const DEFAULT_CA_SETTINGS: PartialCaComparisonTaxSettings = {};

export type UsTaxAnalysisInputValues = PartialUsComparisonTaxSettings;
export type CaTaxAnalysisInputValues = PartialCaComparisonTaxSettings;
export type TaxAnalysisInputValues = UsTaxAnalysisInputValues | CaTaxAnalysisInputValues;

/** Discriminated union for region-specific tax inputs */
export type RegionTaxInputs =
  | { region: typeof TaxRegionKey.US; values: UsTaxAnalysisInputValues }
  | { region: typeof TaxRegionKey.CA; values: CaTaxAnalysisInputValues };

export const FIELD_KEYS = {
  [TaxRegionKey.US]: {
    SHORT_TERM_YTD_GAIN: 'shortTermYTDGain',
    SHORT_TERM_YTD_LOSS: 'shortTermYTDLoss',
    LONG_TERM_YTD_GAIN: 'longTermYTDGain',
    LONG_TERM_YTD_LOSS: 'longTermYTDLoss',
    SHORT_TERM_CARRIED_FORWARD_LOSS: 'shortTermCarriedForwardLoss',
    LONG_TERM_CARRIED_FORWARD_LOSS: 'longTermCarriedForwardLoss',
    SHORT_TERM_CAPITAL_GAINS_RATE: 'shortTermCapitalGainsRate',
    LONG_TERM_CAPITAL_GAINS_RATE: 'longTermCapitalGainsRate',
  },
  [TaxRegionKey.CA]: {
    YTD_GAIN: 'ytdGain',
    YTD_LOSS: 'ytdLoss',
    CARRY_FORWARD_LOSS: 'carryForwardLoss',
    MARGINAL_TAX_RATE: 'marginalTaxRate',
  },
} as const;

export const FIELD_LABEL_KEYS = {
  [TaxRegionKey.US]: {
    [FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_YTD_GAIN]: 'Comparison.TaxAnalysis.ShortTermYTDGain',
    [FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_YTD_LOSS]: 'Comparison.TaxAnalysis.ShortTermYTDLoss',
    [FIELD_KEYS[TaxRegionKey.US].LONG_TERM_YTD_GAIN]: 'Comparison.TaxAnalysis.LongTermYTDGain',
    [FIELD_KEYS[TaxRegionKey.US].LONG_TERM_YTD_LOSS]: 'Comparison.TaxAnalysis.LongTermYTDLoss',
    [FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_CARRIED_FORWARD_LOSS]: 'Comparison.TaxAnalysis.ShortTermCarriedForwardLoss',
    [FIELD_KEYS[TaxRegionKey.US].LONG_TERM_CARRIED_FORWARD_LOSS]: 'Comparison.TaxAnalysis.LongTermCarriedForwardLoss',
    [FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_CAPITAL_GAINS_RATE]: 'Comparison.TaxAnalysis.ShortTermCapitalGainsRate',
    [FIELD_KEYS[TaxRegionKey.US].LONG_TERM_CAPITAL_GAINS_RATE]: 'Comparison.TaxAnalysis.LongTermCapitalGainsRate',
  },
  [TaxRegionKey.CA]: {
    [FIELD_KEYS[TaxRegionKey.CA].YTD_GAIN]: 'Comparison.TaxAnalysis.YTDGain',
    [FIELD_KEYS[TaxRegionKey.CA].YTD_LOSS]: 'Comparison.TaxAnalysis.YTDLoss',
    [FIELD_KEYS[TaxRegionKey.CA].CARRY_FORWARD_LOSS]: 'Comparison.TaxAnalysis.CarryForwardLoss',
    [FIELD_KEYS[TaxRegionKey.CA].MARGINAL_TAX_RATE]: 'Comparison.TaxAnalysis.MarginalTaxRate',
  },
} as const;

export const useTaxAnalysisInputs = () => {
  const dispatch = useDispatch<Dispatch>();
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  // TODO: Uncomment when implementing tax analysis for CA region
  // const userRegion = useSelector((state: ReduxState) => state.auth.user?.appConfig?.appRegion);
  // const region = userRegion === 'CAN' ? TaxRegionKey.CA : TaxRegionKey.US;
  const region = TaxRegionKey.US; // Temporary hardcoding to US region until CA tax analysis is implemented
  const initialized = useRef(false);

  const taxSettings = activeComparison?.taxAnalysis?.taxSettings;

  const usSettings = taxSettings?.[TaxRegionKey.US] ?? {};
  const caSettings = taxSettings?.[TaxRegionKey.CA] ?? {};

  const defaultUsSettings: PartialUsComparisonTaxSettings = { ...DEFAULT_US_SETTINGS, ...usSettings };
  const defaultCaSettings: PartialCaComparisonTaxSettings = { ...DEFAULT_CA_SETTINGS, ...caSettings };

  // Initialize defaults in Redux once
  useEffect(() => {
    if (!initialized.current && activeComparison) {
      const defaultSettingsByRegion = {
        [TaxRegionKey.US]: () => dispatch(updateComparisonTaxSettings({ region, [TaxRegionKey.US]: defaultUsSettings })),
        [TaxRegionKey.CA]: () => dispatch(updateComparisonTaxSettings({ region, [TaxRegionKey.CA]: defaultCaSettings })),
      };
      defaultSettingsByRegion[region]();
      initialized.current = true;
    }
  }, [activeComparison, region, dispatch, defaultUsSettings, defaultCaSettings]);

  // Build region-specific inputs as a discriminated union
  const regionInputs: RegionTaxInputs =
    region === TaxRegionKey.US ? { region: TaxRegionKey.US, values: usSettings } : { region: TaxRegionKey.CA, values: caSettings };

  // Create setters that update the region-specific settings
  const createUsSetter = useCallback(
    (key: keyof PartialUsComparisonTaxSettings) => (value?: number) => {
      if (!activeComparison) return;
      const updatedSettings: PartialUsComparisonTaxSettings = { ...usSettings, [key]: value };
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.US, [TaxRegionKey.US]: updatedSettings }));
    },
    [activeComparison, dispatch, usSettings],
  );

  const createCaSetter = useCallback(
    (key: keyof PartialCaComparisonTaxSettings) => (value?: number) => {
      if (!activeComparison) return;
      const updatedSettings: PartialCaComparisonTaxSettings = { ...caSettings, [key]: value };
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.CA, [TaxRegionKey.CA]: updatedSettings }));
    },
    [activeComparison, dispatch, caSettings],
  );

  return {
    regionInputs,
    // US setters
    setShortTermYTDGain: createUsSetter(FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_YTD_GAIN),
    setShortTermYTDLoss: createUsSetter(FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_YTD_LOSS),
    setLongTermYTDGain: createUsSetter(FIELD_KEYS[TaxRegionKey.US].LONG_TERM_YTD_GAIN),
    setLongTermYTDLoss: createUsSetter(FIELD_KEYS[TaxRegionKey.US].LONG_TERM_YTD_LOSS),
    setShortTermCarriedForwardLoss: createUsSetter(FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_CARRIED_FORWARD_LOSS),
    setLongTermCarriedForwardLoss: createUsSetter(FIELD_KEYS[TaxRegionKey.US].LONG_TERM_CARRIED_FORWARD_LOSS),
    setShortTermCapitalGainsRate: createUsSetter(FIELD_KEYS[TaxRegionKey.US].SHORT_TERM_CAPITAL_GAINS_RATE),
    setLongTermCapitalGainsRate: createUsSetter(FIELD_KEYS[TaxRegionKey.US].LONG_TERM_CAPITAL_GAINS_RATE),
    // CA setters
    setYTDGain: createCaSetter(FIELD_KEYS[TaxRegionKey.CA].YTD_GAIN),
    setYTDLoss: createCaSetter(FIELD_KEYS[TaxRegionKey.CA].YTD_LOSS),
    setCarryForwardLoss: createCaSetter(FIELD_KEYS[TaxRegionKey.CA].CARRY_FORWARD_LOSS),
    setMarginalTaxRate: createCaSetter(FIELD_KEYS[TaxRegionKey.CA].MARGINAL_TAX_RATE),
  };
};
